import { engine } from '@play-co/timestep-core';
import { MaskView, View, animate } from '@play-co/timestep-core/ui';
import TutorialHand from 'src/game/components/shared/TutorialHand';
import NakedPromise from 'src/game/minigame/match2-timestep/lib/pattern/NakedPromise';
import { captureGenericError } from 'src/lib/sentry';
import {
  getApplication,
  getScreenDimensions,
  getScreenExtraVerticalSpace,
} from 'src/lib/utils';

export type TutorialHandServiceShowArgs = {
  tag: string;
  x: number;
  y: number;
  zIndex?: number;
  reversed?: boolean;
  autoHide?: boolean;
};

export class TutorialHandService {
  private parent: View;
  private mask: MaskView | undefined;
  private fill: View;
  private clickTarget: View;
  private hand: TutorialHand;
  private curentTarget?: View;
  private showing = false;
  private autoHide = true;
  private tapPromise?: NakedPromise;

  constructor() {
    // Create parent view.
    let superview = new View();

    // Ignore errors if we cannot get the root view.
    try {
      superview = getApplication().getRootView();
    } catch (e) {
      console.error(e);
      captureGenericError('Cannot get root view for tutorial hand service', e);
    }

    this.parent = new View({
      superview,
      visible: false,
    });

    // Create overlay.
    try {
      this.mask = new MaskView({
        superview: this.parent,
        mask: 'assets/ui/tutorial/tutorial_rect_mask.png',
        width: 1,
        height: 1,
        opacity: 0.7,
      });
    } catch (e) {
      console.error(e);
    }

    // Create fill.
    this.fill = new View({
      superview: this.mask ?? this.parent,
      backgroundColor: 'rgba(0, 0, 0, 1)',
      opacity: 0,
    });

    // Create click target.
    this.clickTarget = new View({
      superview: this.parent,
      canHandleEvents: false,
    });

    // Pass events through to target.
    this.clickTarget.onInputStart = (...args) => {
      this.curentTarget?.onInputStart?.bind(this.curentTarget)(...args);
    };
    this.clickTarget.onInputSelect = (...args) => {
      if (this.autoHide) void this.hide();
      this.tapPromise?.resolve();
      this.curentTarget?.onInputSelect?.bind(this.curentTarget)(...args);
    };
    this.clickTarget.onInputOut = (...args) => {
      this.curentTarget?.onInputOut?.bind(this.curentTarget)(...args);
    };
    this.clickTarget.onInputOver = (...args) => {
      this.curentTarget?.onInputOver?.bind(this.curentTarget)(...args);
    };
    this.clickTarget.onInputMove = (...args) => {
      this.curentTarget?.onInputMove?.bind(this.curentTarget)(...args);
    };

    // Create hand.
    this.hand = new TutorialHand({
      superview: this.clickTarget,
    });

    // Tick to updat.onInputSelect
    engine.on('Tick', this.tick.bind(this));
  }

  public async show(args: TutorialHandServiceShowArgs) {
    const { tag, x, y } = args;
    const zIndex = args.zIndex ?? 1000;
    const reversed = args.reversed ?? false;
    this.autoHide = args.autoHide ?? true;
    this.tapPromise = new NakedPromise();

    if (!handTargets[tag]) {
      // Do not throw error in production, carry on.
      if (process.env.IS_DEVELOPMENT) {
        throw new Error(`Hand target ${tag} not found`);
      }

      captureGenericError(
        'Cannot display tutorial hand',
        new Error(`Hand target ${tag} not found`),
      );

      return;
    }

    this.showing = true;
    this.curentTarget = handTargets[tag];
    this.parent.updateOpts({
      zIndex,
      visible: true,
    });
    if (this.mask) {
      // Only fade in if we have a mask, leave it transparent otherwise.
      animate(this.fill).then({ opacity: 1 }, 500);
    }
    await this.hand.fadeIn(x, y, reversed);
    this.clickTarget.updateOpts({ canHandleEvents: true });
  }

  public async hide() {
    this.clickTarget.updateOpts({ canHandleEvents: false });
    this.showing = false;
    await this.hand.fadeOut();
    await animate(this.fill).then({ opacity: 0 }, 300).toPromise();
    this.parent.hide();
    delete this.curentTarget;
  }

  /**
   * Allow clearing current target without removing the blocking overlay which could be useful
   * for a sequence of targets where we want the screen to be cleared but not interactive.
   * Ex.: show -> aks for action -> clear target -> wait something -> show again
   */
  public async clearScreen() {
    this.hand.fadeOut();
    await animate(this.fill).then({ opacity: 0 }, 300).toPromise();
  }

  private tick() {
    if (!this.showing) return;

    const root = getApplication().getRootView();
    const width = root.style.width;
    const height = getScreenDimensions().height;
    const offsetY = getScreenExtraVerticalSpace() / 2;

    this.parent.updateOpts({
      width: width,
      height: height,
      y: -offsetY,
      visible: true,
    });

    this.fill.updateOpts({
      width: width,
      height: height,
    });

    // Only update click target and mask if the target is in root.
    if (this.curentTarget?.getSuperviews().includes(root)) {
      const position = this.curentTarget.getPosition(root);
      position.y += offsetY;
      const scale = 1.75;
      this.mask?.updateOpts({
        width: width,
        height: height,
        maskTransform: {
          x: position.x - (position.width * scale - position.width) / 2,
          y: position.y - (position.height * scale - position.height) / 2,
          width: position.width * scale,
          height: position.height * scale,
          rotation: position.r,
        },
      });

      this.clickTarget.updateOpts(position);
    }
  }

  public waitForTap() {
    return this.tapPromise;
  }
}

const handTargets: Record<string, View> = {};

export function getHandTarget(tag: string) {
  return handTargets[tag];
}

/**
 * Register a hand target to be used by the tutorial hand.
 * @param tag
 * @param view
 */
export function registerHandTarget(tag: string, view: View) {
  // Skip if already registered
  if (handTargets[tag]) return;
  handTargets[tag] = view;

  view.on('ViewRemoved', () => {
    // Not the current target, ignore.
    if (handTargets[tag] !== view) return;
    delete handTargets[tag];
  });
}
